import React, { useState } from 'react';
import { useBigQueryContext } from '../contexts/BigQueryContext';

type Props = {
  nextStep: () => void;
};

const SA_EMAIL_REGEX = /^[^@\s]+@[^@\s]+\.iam\.gserviceaccount\.com$/;

function normalizePrivateKey(key: string): string {
  return key.replace(/\\n/g, '\n').replace(/\r\n/g, '\n');
}

export function ServiceAccountStep({ nextStep }: Props) {
  const { credentials, setCredentials } = useBigQueryContext();
  const [jsonText, setJsonText] = useState('');
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = () => {
    setError(null);

    if (jsonText.length > 32_768) {
      setError('Content too large to be a service account key.');
      return;
    }

    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(jsonText.trim());
    } catch {
      setError(
        'Invalid JSON — please paste the full contents of the service account key file.'
      );
      return;
    }

    if (parsed.type !== 'service_account') {
      setError(
        'This does not appear to be a service account key (missing or wrong "type" field).'
      );
      return;
    }

    const clientEmail = parsed.client_email;
    const privateKey = parsed.private_key;

    if (typeof clientEmail !== 'string' || !clientEmail) {
      setError('Missing client_email in the service account key.');
      return;
    }

    if (!SA_EMAIL_REGEX.test(clientEmail)) {
      setError(
        'client_email does not look like a service account address (expected format: name@project.iam.gserviceaccount.com).'
      );
      return;
    }

    if (typeof privateKey !== 'string' || !privateKey) {
      setError('Missing private_key in the service account key.');
      return;
    }

    const normalizedKey = normalizePrivateKey(privateKey);

    if (!normalizedKey.includes('-----BEGIN PRIVATE KEY-----')) {
      setError('private_key does not appear to be a valid PEM-encoded key.');
      return;
    }

    const saProjectId =
      typeof parsed.project_id === 'string' ? parsed.project_id : undefined;

    setCredentials({
      ...credentials,
      auth_type: 'service_account',
      tokens: undefined,
      service_account: {
        client_email: clientEmail,
        private_key: normalizedKey,
        project_id: saProjectId,
      },
    });

    nextStep();
  };

  return (
    <>
      <div className="row justify-content-center">
        <div className="col-8">
          <div className="card box-shadow-3d mb-3">
            <div className="card-header">
              <div className="d-flex align-items-center">
                <img
                  className="con-map-mk-integration-logo-size"
                  style={{ width: '30px' }}
                  src="/media/integrations/logos/bigquery-icon.png"
                />
                <h5 className="mb-0 ml-3">BigQuery — Service Account Key</h5>
              </div>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-8">
                  <h5>Paste your service account JSON key</h5>
                  <p className="mt-3">
                    In Google Cloud Console, create a service account and grant
                    it two roles:
                  </p>
                  <ul>
                    <li>
                      <strong>BigQuery Data Viewer</strong> on your dataset —
                      allows reading your data.
                    </li>
                    <li>
                      <strong>BigQuery Job User</strong> on the project — allows
                      running query jobs.
                    </li>
                  </ul>
                  <p>
                    Then generate a JSON key and paste the full contents below.
                  </p>
                  <textarea
                    className="form-control mt-3"
                    rows={10}
                    placeholder='{ "type": "service_account", "client_email": "...", "private_key": "..." }'
                    value={jsonText}
                    onChange={(e) => setJsonText(e.target.value)}
                  />
                  {error && (
                    <div className="alert alert-danger mt-2" role="alert">
                      {error}
                    </div>
                  )}
                </div>
                <div className="col-md-4">
                  <b className="d-block">Links</b>
                  <a
                    href="https://help.madkudu.com/docs/bigquery-1"
                    target="_blank"
                  >
                    How to connect BigQuery to HG Insights
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="mt-3 pb-4 text-center">
        <button
          className="btn btn-primary"
          disabled={!jsonText.trim()}
          onClick={handleSubmit}
        >
          Next
        </button>
      </div>
    </>
  );
}
